import { Edge, Node } from '@xyflow/react';
import { EdgeHandles, Tree, TreeNodeKind, TreeNodeType } from './types';

const nodeWidth = 150;
const nodeHeight = 36;

const relationshipNodeWidth = 50;

const Orientation = { Vertical: 'vertical', Horizontal: 'horizontal' } as const;

const EdgeType = {
  Primary: 'primary',
  Secondary: 'secondary',
} as const;

type EdgeType = (typeof EdgeType)[keyof typeof EdgeType];

const RelationType = {
  ParentChild: 'parentChild',
  SpouseSpouse: 'spouseSpouse',
  ExSpouseExSpouse: 'exSpouseExSpouse',
} as const;

type RelationType = (typeof RelationType)[keyof typeof RelationType];

function addEdge(
  edges: Edge<Record<string, unknown>, string | undefined>[],
  edgeKeys: Set<string>,
  source: string,
  target: string,
  relationType: RelationType,
  edgeType: EdgeType
) {
  const key = `${source}>${target}:${relationType}:${edgeType}`;
  if (edgeKeys.has(key)) return;
  edgeKeys.add(key);

  let sourceHandle: EdgeHandles;
  let targetHandle: EdgeHandles;
  if (relationType === RelationType.ParentChild) {
    sourceHandle = EdgeHandles.vertical.source;
    targetHandle = EdgeHandles.vertical.target;
  } else if (relationType === RelationType.SpouseSpouse) {
    if (edgeType === EdgeType.Primary) {
      sourceHandle = EdgeHandles.horizontalLeft.source;
      targetHandle = EdgeHandles.horizontalLeft.target;
    } else {
      sourceHandle = EdgeHandles.horizontalLeft.target;
      targetHandle = EdgeHandles.horizontalLeft.source;
    }
  } else if (relationType === RelationType.ExSpouseExSpouse) {
    if (edgeType === EdgeType.Primary) {
      sourceHandle = EdgeHandles.horizontalRight.source;
      targetHandle = EdgeHandles.horizontalRight.target;
    } else {
      sourceHandle = EdgeHandles.horizontalRight.target;
      targetHandle = EdgeHandles.horizontalRight.source;
    }
  } else {
    throw new Error(`Unknown relation type: ${relationType}`);
  }

  edges.push({
    id: key,
    source: source,
    target: target,
    type: 'smoothstep',
    animated: true,
    // animated: edgeType === EdgeType.Primary,
    sourceHandle,
    targetHandle,
    style: { strokeDasharray: '4 4', opacity: 0.7 },
    // edgeType === EdgeType.Primary
    //   ? { strokeDasharray: '4 4', opacity: 0.7 }
    //   : undefined,
  });
}

function parseTree(tree: Tree): {
  nodes: TreeNodeType[];
  edges: { source: TreeNodeType; target: TreeNodeType }[];
} {
  const nodes: TreeNodeType[] = [];
  const edges: { source: TreeNodeType; target: TreeNodeType }[] = [];

  for (const nodeId in tree) {
    const node = tree[nodeId];
    nodes.push(node);
    const childIds = node.children || [];
    for (const childId of childIds) {
      const childNode = tree[childId];
      if (childNode) {
        edges.push({ source: node, target: childNode });
      }
    }
    const spouseIds = node.spouses || [];
    for (const spouseId of spouseIds) {
      const spouseNode = tree[spouseId];
      if (spouseNode) {
        edges.push({ source: node, target: spouseNode });
      }
    }
    const exSpouseIds = node.exSpouses || [];
    for (const exSpouseId of exSpouseIds) {
      const exSpouseNode = tree[exSpouseId];
      if (exSpouseNode) {
        edges.push({ source: node, target: exSpouseNode });
      }
    }
  }

  return { nodes, edges };
}

const InsertionPosition = {
  Before: 'before',
  After: 'after',
} as const;

type InsertionPosition =
  (typeof InsertionPosition)[keyof typeof InsertionPosition];

const calculateLayout = (
  tree: Tree,
  rootId: string
): {
  nodes: (TreeNodeType & {
    id: string;
    x: number;
    y: number;
    name: string;
  })[];
  edges: { source: TreeNodeType; target: TreeNodeType }[];
} => {
  console.log('Calculating layout for tree with rootId:', rootId, tree);

  const rootNode = tree[rootId];
  if (!rootNode) {
    throw new Error(`Root node with id ${rootId} not found in tree.`);
  }

  const levels = new Map<number, TreeNodeType[]>();
  const visited = new Set<string>();

  const traverse = (
    node: TreeNodeType,
    level: number,
    insertionPosition?: InsertionPosition
  ) => {
    if (visited.has(node.id)) return;
    visited.add(node.id);

    if (!levels.has(level)) {
      levels.set(level, []);
    }
    if (insertionPosition === InsertionPosition.Before) {
      levels.get(level)!.unshift(node);
    } else {
      levels.get(level)!.push(node);
    }

    const spouseIds = node.spouses || [];
    for (const spouseId of spouseIds) {
      const spouseNode = tree[spouseId];
      if (spouseNode) {
        traverse(spouseNode, level, InsertionPosition.Before);
      }
    }

    const exSpouseIds = node.exSpouses || [];
    for (const exSpouseId of exSpouseIds) {
      const exSpouseNode = tree[exSpouseId];
      if (exSpouseNode) {
        traverse(exSpouseNode, level, InsertionPosition.After);
      }
    }

    const parentIds = node.parents || [];
    for (const parentId of parentIds) {
      const parentNode = tree[parentId];
      if (parentNode) {
        traverse(parentNode, level - 1);
      }
    }

    const childIds = node.children || [];
    for (const childId of childIds) {
      const childNode = tree[childId];
      if (childNode) {
        traverse(childNode, level + 1);
      }
    }
  };

  traverse(rootNode, 0);

  const { nodes: parsedNodes, edges: parsedEdges } = parseTree(tree);

  const nodes = [];
  for (const [level, levelNodes] of levels.entries()) {
    const levelNodesWidth =
      levelNodes[0].kind === TreeNodeKind.Person
        ? nodeWidth
        : relationshipNodeWidth;

    const y = level * (nodeHeight + 100);
    const totalWidth = levelNodes.length * (levelNodesWidth + 100) - 100; // total width of the level
    let x = -totalWidth / 2; // start x to center the level

    for (const node of levelNodes) {
      nodes.push({ ...node, id: node.id, x, y, name: node.name });
      x += levelNodesWidth + 100; // increment x for next node
    }
  }

  return {
    nodes,
    edges: parsedEdges,
  };
};

export const layoutElements = (
  tree: Tree,
  rootId: string
): {
  nodes: Node<Record<string, unknown>, string | undefined>[];
  edges: Edge<Record<string, unknown>, string | undefined>[];
} => {
  const { nodes: parsedNodes, edges: parsedEdges } = calculateLayout(
    tree,
    rootId
  );

  console.log('Parsed nodes:', parsedNodes);
  console.log('Parsed edges:', parsedEdges);

  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const edgeKeys = new Set<string>();

  parsedNodes.forEach(n => {
    nodes.push({
      id: n.id,
      position: { x: n.x, y: n.y },
      style:
        n.id === 'root'
          ? { width: 0, height: 0 }
          : {
              width: n.kind !== 'person' ? relationshipNodeWidth : nodeWidth,
              height: nodeHeight,
            },
      type: 'custom',
      data: {
        ...n,
        label: n.name, // maybe decide label here or in custom node and not in transformer
      },
    });
  });

  parsedEdges.forEach(e => {
    // console.log('Processing edge', e);

    const relationNode =
      e.source.kind === TreeNodeKind.Person ? e.target : e.source;

    if (!relationNode.kind) return;

    let relationType: RelationType;
    if (relationNode.kind === TreeNodeKind.Person) {
      relationType =
        e.source.isSpouse || e.target.isSpouse
          ? RelationType.SpouseSpouse
          : RelationType.ExSpouseExSpouse;
    } else if (
      relationNode.kind === TreeNodeKind.Marriage ||
      relationNode.kind === TreeNodeKind.Divorce ||
      relationNode.kind === TreeNodeKind.Family ||
      relationNode.kind === TreeNodeKind.Invisible
    ) {
      relationType = RelationType.ParentChild;
    } else {
      throw new Error(
        `Unknown relation node kind: ${relationNode.kind} (id: ${relationNode.id})`
      );
    }

    let edgeType: EdgeType;
    if (relationType === RelationType.ParentChild) {
      edgeType =
        e.source.id === relationNode.id ? EdgeType.Secondary : EdgeType.Primary;
    } else {
      edgeType =
        e.source.isSpouse || e.source.isExSpouse
          ? EdgeType.Secondary
          : EdgeType.Primary;
    }

    addEdge(edges, edgeKeys, e.source.id, e.target.id, relationType, edgeType);
  });

  // console.log('layoutElements nodes', nodes);
  // console.log('layoutElements edges', edges);

  return { nodes, edges };
};
